import ShareDirService from "@/services/shareDir.service";
import ToastService from "@/services/toast.service";

const delay = (ms) => {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
};

export default {
    namespaced: true,
    state: {
        token: null,
        curDir: [],
        fileArray: [],
        selectArray: [],
        currentFolderSize: "",
        folderSizes: {},
        folderSizeRequestId: 0,
        errorMessage: "",
    },
    getters: {
        token: state => state.token,
        fileArray: state => state.fileArray,
        currentDirPath: state => {
            return state.curDir.length ? state.curDir.join("/") : "";
        },
        fileArrayTotalSize: state => {
            return state.fileArray.reduce((sum, file) => {
                return sum + (file.meta?.size || 0);
            }, 0);
        },
        selectArrayTotalSize: state => {
            return state.selectArray.reduce((sum, file) => {
                return sum + (file.meta?.size || 0);
            }, 0);
        },
        getMtimeMsByFileName: state => fileName => {
            const target = state.fileArray.find(item => item.name === fileName);

            return target?.meta?.mtimeMs || 0;
        },
        imageFiles: state => {
            return state.fileArray.filter(file => file.type?.startsWith("image/"));
        },
        errorMessage: state => state.errorMessage,
    },
    mutations: {
        SET_TOKEN(state, token) {
            state.token = token;
        },
        SET_CURRENT_DIR(state, currentDir) {
            state.curDir = currentDir ? currentDir.split("/").filter(Boolean) : [];
        },
        SET_FILE_ARRAY(state, files) {
            state.fileArray = files;
        },
        SET_ERROR_MESSAGE(state, message) {
            state.errorMessage = message;
        },
        CLEAR(state) {
            state.token = null;
            state.curDir = "";
            state.fileArray = [];
            state.errorMessage = "";
        },
        SORT_FILE_ARRAY(state) {
            state.fileArray = state.fileArray.sort((a, b) => {
                const nameA = (a.name || '').toString();
                const nameB = (b.name || '').toString();

                // 폴더 우선 정렬
                if (a.isFile && !b.isFile) return 1;
                if (!a.isFile && b.isFile) return -1;

                // 이름 숫자 기준 자연 정렬
                return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
            });
        },
        removeSelectedTenant(state, name) {
            state.selectArray = state.selectArray.filter(tenant => {
                return tenant.name !== name;
            });
        },
        addSelectedTenant(state, tenant) {
            state.selectArray.push(tenant)
        },
        setCurrentFolderSize(state, size) {
            state.currentFolderSize = size;
        },
        setFolderSize(state, { name, size }) {
            state.folderSizes[name] = size;
        },
    },
    actions: {
        async createShareLink({ commit }) {
            const res = await ShareDirService.createShareLink();

            const token = res.data.token;
            commit("SET_TOKEN", token);

            return token;
        },
        setSortFiles({ commit }) {
            commit('SORT_FILE_ARRAY');
        },
        async fileList({ state, commit }, subDir = "") {
            try {
                const res = await ShareDirService.getShareDirFileList(
                    state.token,
                    subDir
                );

                commit("SET_CURRENT_DIR", res.data.currentDir);
                commit("SET_FILE_ARRAY", res.data.files);
                commit("SORT_FILE_ARRAY");
                commit("SET_ERROR_MESSAGE", "");

                return res.data;
            } catch (error) {
                const message = error.response?.data || "파일 목록을 불러오지 못했습니다.";

                commit("SET_ERROR_MESSAGE", message);
                throw error;
            }
        },
        async createShareLinkToken({ }, { fileName, token, dir }) {
            try {
                const { data } = await ShareDirService.createShareLinkToken({ fileName, token, dir });

                return data;
            } catch (err) {
                console.error('createShareLinkToken error:', err);
                return null;
            }
        },
        updateSelected({ state, getters, commit }, tenant) {
            if (tenant.checked) {
                const file = state.fileArray.find(f => f.name === tenant.value);

                if (file) {
                    commit("addSelectedTenant", {
                        path: getters.currentDirPath,
                        name: file.name,
                        isFile: file.isFile,
                        meta: { ...file.meta }
                    });
                }
            } else {
                commit("removeSelectedTenant", tenant.value);
            }
        },
        async loadCurrentFolderSize({ commit, state, getters }, token) {
            const dir = getters.currentDirPath;

            commit("setCurrentFolderSize", "계산중...");

            try {
                const data = await ShareDirService.getFolderSize(token, dir);

                commit("setCurrentFolderSize", data.size);
            } catch (error) {
                commit("setCurrentFolderSize", "계산 실패");
                console.error(error);
            }
        },
        async loadFolderSize({ commit, state, getters }, item) {
            const dir = getters.currentDirPath;
            const subDir = dir
                ? `${dir}/${item.name}`
                : item.name;

            commit("setFolderSize", {
                name: item.name,
                size: "계산중..."
            });

            try {
                const data = await ShareDirService.getFolderSize(
                    state.token,
                    subDir
                );
                commit("setFolderSize", {
                    name: item.name,
                    size: data.size
                });
            } catch (error) {
                commit("setFolderSize", {
                    name: item.name,
                    size: "계산 실패"
                });
                console.error(error);
            }
        },
        async fileDown({ state }, files) {
            if (!files || files.length === 0) return;
            const dir = state.curDir.join("/");
            for (const file of files) {
                try {
                    const response = await ShareDirService.fileDown(
                        file,
                        state.token,
                        dir
                    );

                    if (!response || response.status !== 200) {
                        throw new Error("Download Error");
                    }
                    const url = response.data.url;
                    const finalUrl = new URL(url, process.env.VUE_APP_API_URL).href;

                    window.open(finalUrl, "_blank");
                } catch (error) {
                    ToastService.error(
                        `${file.name} 다운로드에 실패했습니다.`
                    );

                    console.error("공유 다운로드 요청 실패:", {
                        status: error.response?.status,
                        data: error.response?.data,
                        request: error.config?.url
                    });

                    throw error;
                }
                await delay(500);
            }
        }
    },
};